import { CheckRounded, PriorityHighRounded } from "@mui/icons-material";
import { Chip, Divider, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { TextViewer } from "src/components";
import { UserFeedbackFilterByOption } from "src/constants";
import { useGetFormSubmissions, type GetOneOnOneMeetingDto, type MeetingAgendaItemDto } from "src/http";
import { getFormSubmissionName } from "src/utilities";

interface Props {
  agendaItem: MeetingAgendaItemDto;
  meeting: GetOneOnOneMeetingDto;
}

const UserFeedbackAgendaStepperContent = (props: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const formSubmissionGuid = searchParams.get("formSubmissionGuid");
  const { data, isLoading } = useGetFormSubmissions({
    filters: {
      [UserFeedbackFilterByOption.Receiver]: [props.meeting.otherUser.userId],
      [UserFeedbackFilterByOption.RequiresReviewInOneOnOne]: ["true"],
      [UserFeedbackFilterByOption.ExcludeDrafts]: ["true"],
    },
  });
  const theme = useTheme();

  const [formSubmissionGuidsForReview, setFormSubmissionGuidsForReview] = useState<Set<string> | null>(null);
  if (data !== undefined && formSubmissionGuidsForReview === null) {
    setFormSubmissionGuidsForReview(
      new Set(data.filter((fs) => !fs.reviewedInOneOnOne).map((fs) => fs.formSubmissionGuid)),
    );
  }

  const formSubmissionsForReview = data?.filter(
    (fs) => !fs.reviewedInOneOnOne || formSubmissionGuidsForReview?.has(fs.formSubmissionGuid),
  );

  return (
    <Stack spacing={0.5} sx={{ pb: 0.5 }}>
      <TextViewer value={`<p>${props.agendaItem.description}</p>`} />
      <Divider variant="dashed" />

      {isLoading || data === undefined ? (
        <Stack spacing={0.5} divider={<Divider variant="dashed" />}>
          <Skeleton variant="rectangular" />
          <Skeleton variant="rectangular" />
        </Stack>
      ) : formSubmissionsForReview && formSubmissionsForReview.length > 0 ? (
        <Stack spacing={0.5} divider={<Divider variant="dashed" />}>
          {formSubmissionsForReview.map((formSubmission) => (
            <Stack
              key={formSubmission.formSubmissionGuid}
              spacing={0.25}
              onClick={() =>
                setSearchParams((searchParams) => {
                  searchParams.set("formSubmissionGuid", formSubmission.formSubmissionGuid);
                  return searchParams;
                })
              }
              sx={{ cursor: "pointer" }}
            >
              {!formSubmission.reviewedInOneOnOne && (
                <Chip label="Review In 1:1 Pending" color="warning" icon={<PriorityHighRounded fontSize="small" />} />
              )}

              {formSubmission.reviewedInOneOnOne && (
                <Chip label="Reviewed in 1:1" variant="tertiary" icon={<CheckRounded fontSize="small" />} />
              )}

              <Stack spacing={-0.25}>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      formSubmissionGuid === formSubmission.formSubmissionGuid ? theme.palette.primary.main : undefined,
                  }}
                >
                  {getFormSubmissionName(
                    formSubmission.formName,
                    formSubmission.dateRangeStartUtc,
                    formSubmission.dateRangeEndUtc,
                  )}
                </Typography>
                <Typography variant="caption">Submitted by {formSubmission.createdByUser.displayName}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="placeholder">Nothing here yet.</Typography>
      )}
    </Stack>
  );
};

export { UserFeedbackAgendaStepperContent };
